
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle } from "lucide-react";

interface RegisterFormProps {
  userType?: 'company' | 'invited';
}

export function RegisterForm({ userType = 'company' }: RegisterFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    
    const requiredFields = userType === 'company' 
      ? [name, email, password, passwordConfirm, agencyName]
      : [name, email, password, passwordConfirm];
    
    if (requiredFields.some(field => !field)) {
      setErrorMsg("Por favor, preencha todos os campos");
      toast.error("Por favor, preencha todos os campos");
      return;
    }
    
    try {
      setLoading(true);
      
      if (password.length < 6) {
        throw new Error("A senha deve ter pelo menos 6 caracteres");
      }
      
      if (password !== passwordConfirm) {
        throw new Error("As senhas não coincidem");
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
            agency_name: userType === 'company' ? agencyName : undefined,
            user_type: userType
          }
        }
      });
      
      if (error) throw error;
      
      console.log("Registration successful:", data);
      
      if (data?.user) {
        toast.success("Cadastro realizado com sucesso! Você já pode fazer login.");
        navigate("/login");
      } else {
        throw new Error("Falha ao criar usuário, tente novamente.");
      }
    } catch (error: any) {
      console.error("Erro ao realizar cadastro:", error);
      
      let message = "Erro ao realizar cadastro";
      
      if (error.message) {
        if (error.message.includes("Email already registered")) {
          message = "Este email já está registrado";
        } else {
          message = error.message;
        }
      }
      
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">
          Criar conta
        </CardTitle>
        <CardDescription>
          Preencha os dados abaixo para criar sua conta de empresa
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRegister} className="space-y-4">
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 text-red-600" />
              <span>{errorMsg}</span>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name"
              placeholder="Seu nome completo"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agencyName">Nome da agência</Label>
            <Input
              id="agencyName"
              placeholder="Nome da sua agência"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
            <p className="text-xs text-muted-foreground">A senha deve ter pelo menos 6 caracteres</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="passwordConfirm">Confirmar senha</Label>
            <Input
              id="passwordConfirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
            {loading ? "Cadastrando..." : "Cadastrar"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col">
        <div className="text-sm text-center mt-2">
          Já tem uma conta?{" "}
          <Link
            to="/login"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Faça login
          </Link>
        </div>
      </CardFooter>
    </Card>
  );
}
